import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Warehouse, DollarSign } from 'lucide-react';

const FTZvsBondedWarehouse = () => {
  const comparisonData = [
    {
      category: "Primary Purpose",
      ftz: "Defer, reduce, or eliminate customs duties on imports",
      bonded: "Defer duties until goods enter U.S. commerce"
    },
    {
      category: "Customs Territory Status",
      ftz: "Outside U.S. customs territory for duty purposes",
      bonded: "Within U.S. customs territory - under customs custody"
    },
    {
      category: "Duty Elimination",
      ftz: "Yes - on re-exported goods and waste/scrap",
      bonded: "No - duties assessed on all goods entering commerce"
    },
    {
      category: "Manufacturing Allowed",
      ftz: "Yes - extensive manufacturing and processing permitted",
      bonded: "Limited - only minor assembly/repackaging"
    },
    {
      category: "Inverted Tariff Relief",
      ftz: "Yes - can pay lower duty rate on finished product",
      bonded: "No - must pay duty on components as imported"
    },
    {
      category: "Time Limit for Storage",
      ftz: "No time limit",
      bonded: "5 years maximum (3 years for most bonded warehouses)"
    },
    {
      category: "Weekly Entry Option",
      ftz: "Yes - consolidate entries for administrative efficiency",
      bonded: "No - individual entry for each import"
    },
    {
      category: "Application Process",
      ftz: "Requires FTZ Board approval (can take 6-18 months)",
      bonded: "CBP approval required (generally faster)"
    },
    {
      category: "Setup Complexity",
      ftz: "More complex - requires zone activation and procedures",
      bonded: "Less complex - standard bonded warehouse procedures"
    },
    {
      category: "Operating Costs",
      ftz: "Higher initial setup, but ongoing savings for high-volume operations",
      bonded: "Lower setup costs, but no duty savings"
    },
    {
      category: "Best For",
      ftz: "Manufacturers, high-volume importers, companies with significant re-exports",
      bonded: "Temporary storage before duty payment, distribution operations"
    },
    {
      category: "State/Local Tax Benefits",
      ftz: "Potential exemption from state/local taxes on goods in zone",
      bonded: "No state/local tax benefits"
    }
  ];

  return (
    <div className="comparison-page">
      <SEO 
        title="Foreign Trade Zone vs Bonded Warehouse: Complete Comparison"
        description="Compare Foreign Trade Zones and Bonded Warehouses. Learn duty deferral, manufacturing capabilities, and cost-benefit analysis for each option."
        keywords="foreign trade zone, bonded warehouse, FTZ vs bonded, duty deferral, customs warehouse, trade zone benefits"
        path="/ftz-vs-bonded-warehouse"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'FTZ vs Bonded Warehouse', path: '/ftz-vs-bonded-warehouse' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Foreign Trade Zone vs Bonded Warehouse
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Both Foreign Trade Zones (FTZs) and bonded warehouses offer duty deferral, but they differ significantly in benefits, capabilities, and complexity. This guide helps you determine which option best suits your business needs.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <Warehouse className="h-8 w-8 text-purple-600" />
                <h3 className="text-2xl font-bold text-purple-900">Foreign Trade Zone</h3>
              </div>
              <p className="text-purple-800 mb-4 font-semibold">Outside customs territory - Maximum benefits</p>
              <ul className="space-y-2 text-purple-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Duty elimination</strong> on re-exports</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Manufacturing</strong> allowed</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Inverted tariff</strong> relief</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span><strong>No time limit</strong> for storage</span>
                </li>
              </ul>
            </div>
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="h-8 w-8 text-orange-600" />
                <h3 className="text-2xl font-bold text-orange-900">Bonded Warehouse</h3>
              </div>
              <p className="text-orange-800 mb-4 font-semibold">In customs territory - Basic duty deferral</p>
              <ul className="space-y-2 text-orange-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Duty deferral</strong> only</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Limited</strong> processing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span><strong>No duty</strong> reduction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span><strong>5-year</strong> storage limit</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Complete Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="text-left p-4 font-bold text-lg">Feature</th>
                  <th className="text-left p-4 font-bold text-lg">Foreign Trade Zone</th>
                  <th className="text-left p-4 font-bold text-lg">Bonded Warehouse</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-900 border-b border-gray-200">
                      {row.category}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.ftz}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.bonded}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            When to Choose Each Option
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Choose FTZ If You:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold text-xl">✓</span>
                  <span>Import $5M+ annually and need significant duty savings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold text-xl">✓</span>
                  <span>Perform manufacturing or substantial transformation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold text-xl">✓</span>
                  <span>Re-export significant portions of your inventory</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold text-xl">✓</span>
                  <span>Want to benefit from inverted tariff rates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold text-xl">✓</span>
                  <span>Need long-term storage without time limits</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Choose Bonded Warehouse If You:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold text-xl">✓</span>
                  <span>Need simple duty deferral for distribution</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold text-xl">✓</span>
                  <span>Have lower import volumes (under $5M annually)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold text-xl">✓</span>
                  <span>Want faster setup and lower initial costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold text-xl">✓</span>
                  <span>Don't need manufacturing capabilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 font-bold text-xl">✓</span>
                  <span>Storage needs are temporary (under 3-5 years)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Not Sure Which Option Is Right for You?
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            We can conduct a cost-benefit analysis and help you determine whether an FTZ or bonded warehouse best suits your business operations.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
                Schedule FTZ Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/services/foreign-trade-zones">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 h-14 font-semibold">
                Learn About FTZ Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FTZvsBondedWarehouse;